// RequireRole — the role half of the route guard. RequireAuth answers "is there a
// session?"; this answers "is this session allowed on this route?". Together with
// <Can> (inline gates) these are the ONLY two places authorization lives
// (FRONTEND_RULES Rule 5) — no ad-hoc role checks in components.
//
// Nest it inside RequireAuth so the session is already settled:
//   <RequireAuth><RequireRole role="sponsor"><SponsorLayout /></RequireRole></RequireAuth>
//
// This is a UX guard, not the security boundary: the API rejects a participant calling
// a sponsor endpoint with 403 regardless of what the client renders.
import PropTypes from 'prop-types';
import { Navigate } from 'react-router-dom';
import { Spinner } from '../components/reusable';
import { useAuth } from './AuthProvider';

export function RequireRole({ role, redirectTo = '/', children }) {
  const { status, role: currentRole } = useAuth();

  // `role` defaults to 'participant' before /me resolves, so redirecting while the
  // session is still loading would bounce a sponsor off their own route on every
  // refresh. Hold instead.
  if (status === 'loading') {
    return <Spinner size="lg" label="Loading your account" />;
  }

  if (currentRole !== role) {
    return <Navigate to={redirectTo} replace />;
  }

  return children;
}

RequireRole.propTypes = {
  /** The role this route requires. */
  role: PropTypes.oneOf(['participant', 'sponsor']).isRequired,
  /** Where to send someone who has the wrong role. */
  redirectTo: PropTypes.string,
  children: PropTypes.node,
};

export default RequireRole;
